import { useEffect, useState, useMemo } from "react";
import { OffersCollection } from "../components/blocks/offers-collection";
import { determineTierIndex, isUserAStaff } from "../utils/loyalty";
import useAuth from "../hooks/use-auth";
import { VinoUser } from "../contexts/auth/state";
import vinoFetch from "../utils/vinoFetch";
import { isEqual } from "date-fns";
import { Box, Text } from "@vinomofo/components";
import { useLoyalty } from "../hooks/account/use-loyalty";

const test = ({ __typename }: { __typename: string }): boolean =>
  __typename === "VinoData_OffersCollection";

const create = ({ collections, pageId, id }): JSX.Element => {
  const { user, isLoading: authLoading } = useAuth();
  const { loyalty, showLoyalty, isLoading: loyaltyLoading } = useLoyalty();

  const [offers, setOffers] = useState(collections);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(true);

  const { tier } = determineTierIndex(
    user as VinoUser,
    showLoyalty,
    loyalty?.loyalty_tier
  );
  const customerLoyaltyTier = tier?.toUpperCase();

  // Fetch updated offers if they change
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setIsFetching(true);
        const res = await vinoFetch.get(
          `/api/refresh-offers-collection?id=${pageId}`
        );
        const updated = res.data.find((c) => c.id === id)?.collections;
        if (updated && !isEqual(offers, updated)) {
          setOffers(updated);
        }
        setIsFetching(false);
      } catch (error) {
        console.error("Error refreshing offers:", error);
        setFetchError("Failed to load offers");
        setIsFetching(false);
      }
    };

    fetchOffers();
  }, [pageId, id]);

  // Determine eligible offers with memoization
  const eligibleOffers = useMemo(() => {
    return offers.filter((offer) => {
      const eligibleTiers = offer.offer?.tiersEligible || [];

      if (offer.__typename === "AdBlock" || isUserAStaff(user as VinoUser)) {
        return true;
      }

      if (!user) {
        return eligibleTiers.includes("GUEST") || eligibleTiers.includes("ALL");
      }

      if (showLoyalty && customerLoyaltyTier) {
        return (
          eligibleTiers.includes(customerLoyaltyTier) ||
          eligibleTiers.includes("ALL")
        );
      }

      return eligibleTiers.includes("ALL");
    });
  }, [
    offers,
    customerLoyaltyTier,
    user,
    showLoyalty,
    isFetching,
    loyaltyLoading,
  ]);

  if (fetchError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        py={[4, 5]}
        px={[3, 4]}
      >
        <Text variant="sm" textAlign="center">
          {fetchError}
        </Text>
      </Box>
    );
  }

  return (
    <OffersCollection
      eligibleOffers={isFetching ? eligibleOffers.slice(0, 4) : eligibleOffers}
      loading={isFetching}
    />
  );
};

export default { test, create };
